import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import './BrandCatalog.css';
import { useQuote } from '../../context/QuoteContext';
import { parseSpecs, parseFeatures, parseDescription } from '../../utils/specParser';

/* ============================================================
   TYPES
   ============================================================ */
export interface CatalogItem {
  id: string;
  title: string;
  sku?: string;
  images: string[];
  groupKey?: string;
  groupLabel?: string;
  subtitle?: string;
  tags?: string[];
  chipFilters?: Record<string, string | string[]>;
  specs?: Record<string, string | number>;
  features?: string[];
  description?: string;
  rawDescription?: string; // HTML description for spec parsing
  badge?: string;
  pdfUrl?: string;
}

export interface FilterGroupConfig {
  label: string;
  field: string;
}

export interface BrandConfig {
  brandName: string;
  tagline: string;
  accentColor: string;
  accentLight: string;
  accentMid: string;
  groupMode: 'series' | 'flat';
  groupByField?: string;
  filterGroups?: FilterGroupConfig[];
  searchPlaceholder?: string;
  heroPills?: { label: string; value: string }[];
  // legacy compat
  quickFilters?: { label: string; field: string; values: string[] }[];
}

interface Props {
  config: BrandConfig;
  items: CatalogItem[];
}

const PER_PAGE = 28;

/* ============================================================
   ICON HELPERS (inline SVG, no deps)
   ============================================================ */
function IconSearch() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
    </svg>
  );
}
function IconX({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M18 6L6 18M6 6l12 12"/>
    </svg>
  );
}
function IconChevron({ open }: { open: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
      <path d="m6 9 6 6 6-6"/>
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
      <path d="M20 6 9 17l-5-5"/>
    </svg>
  );
}
function IconTag() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
      <circle cx="7" cy="7" r="1" fill="currentColor"/>
    </svg>
  );
}
function IconImage() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <path d="m21 15-5-5L5 21"/>
    </svg>
  );
}
function IconCart() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <path d="M16 10a4 4 0 0 1-8 0"/>
    </svg>
  );
}
function IconCheck2({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M20 6 9 17l-5-5"/>
    </svg>
  );
}
function IconFilter() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
  );
}
function IconPhone() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.42 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.5a16 16 0 0 0 5.59 5.59l1.06-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.28 14l.64 2.92z"/>
    </svg>
  );
}
function IconPdf() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
}

/* ============================================================
   FILTER HELPERS
   ============================================================ */
function getItemFilterValues(item: CatalogItem, fg: FilterGroupConfig): string[] {
  const raw =
    item.chipFilters?.[fg.label] ??
    item.chipFilters?.[fg.field] ??
    (item as unknown as Record<string, string | string[]>)[fg.field];

  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(v => v.trim()).filter(Boolean);
  const trimmed = raw.trim();
  return trimmed ? [trimmed] : [];
}

function itemMatchesFilters(
  item: CatalogItem,
  filterGroups: FilterGroupConfig[],
  activeFilters: Record<string, Set<string>>,
  skipGroup?: string
): boolean {
  for (const fg of filterGroups) {
    if (fg.label === skipGroup) continue;
    const selected = activeFilters[fg.label];
    if (!selected?.size) continue;
    const values = getItemFilterValues(item, fg);
    if (!values.some(v => selected.has(v))) return false;
  }
  return true;
}

function itemMatchesSearch(item: CatalogItem, term: string): boolean {
  if (!term) return true;
  const inTitle = item.title.toLowerCase().includes(term);
  const inSku = item.sku?.toLowerCase().includes(term);
  const inGroup = item.groupKey?.toLowerCase().includes(term);
  const inTags = item.tags?.some(t => t.toLowerCase().includes(term));
  return !!(inTitle || inSku || inGroup || inTags);
}

function buildFilterOptions(
  items: CatalogItem[],
  filterGroups: FilterGroupConfig[],
  activeFilters: Record<string, Set<string>>,
  search: string
): Record<string, { value: string; count: number }[]> {
  const term = search.toLowerCase().trim();
  const out: Record<string, { value: string; count: number }[]> = {};

  for (const fg of filterGroups) {
    const counts = new Map<string, number>();
    for (const item of items) {
      if (!itemMatchesSearch(item, term)) continue;
      if (!itemMatchesFilters(item, filterGroups, activeFilters, fg.label)) continue;
      for (const val of getItemFilterValues(item, fg)) {
        counts.set(val, (counts.get(val) || 0) + 1);
      }
    }
    out[fg.label] = Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, 'tr'));
  }
  return out;
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function BrandCatalog({ config, items }: Props) {
  const { has, toggle: quoteToggle, count: quoteCount, openDrawer } = useQuote();

  // Normalize filter groups from both new and legacy config
  const filterGroups: FilterGroupConfig[] = useMemo(() => {
    if (config.filterGroups) return config.filterGroups;
    if (config.quickFilters) return config.quickFilters.map(qf => ({ label: qf.label, field: qf.field }));
    return [];
  }, [config]);

  /* ─── State ─── */
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, Set<string>>>({});
  const [page, setPage] = useState(1);
  const [openFilterGroups, setOpenFilterGroups] = useState<Set<string>>(() => new Set(filterGroups.map(f => f.label)));
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  const [panelImg, setPanelImg] = useState(0);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  /* ── Filter options (faceted) ── */
  const filterOptions = useMemo(
    () => buildFilterOptions(items, filterGroups, activeFilters, search),
    [items, filterGroups, activeFilters, search]
  );

  const visibleFilterGroups = useMemo(
    () => filterGroups.filter(fg => (filterOptions[fg.label] || []).length > 0),
    [filterGroups, filterOptions]
  );

  /* ── Filtered items ── */
  const filteredItems = useMemo(() => {
    const term = search.toLowerCase().trim();
    return items.filter(item => {
      if (!itemMatchesSearch(item, term)) return false;
      return itemMatchesFilters(item, filterGroups, activeFilters);
    });
  }, [items, search, activeFilters, filterGroups]);

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paged = filteredItems.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileSidebarOpen]);

  /* ── Active filter count ── */
  const activeFilterCount = useMemo(() =>
    Object.values(activeFilters).reduce((sum, s) => sum + s.size, 0),
    [activeFilters]
  );

  /* ── Handlers ── */
  const toggleFilter = useCallback((label: string, value: string) => {
    setActiveFilters(prev => {
      const next = { ...prev };
      const current = new Set(prev[label] || []);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      next[label] = current;
      return next;
    });
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters({});
    setSearch('');
    setPage(1);
    setMobileSidebarOpen(false);
  }, []);

  const closeMobileFilters = useCallback(() => {
    setMobileSidebarOpen(false);
  }, []);

  const openItem = useCallback((item: CatalogItem) => {
    setSelectedItem(item);
    setPanelImg(0);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeItem = useCallback(() => {
    setSelectedItem(null);
    document.body.style.overflow = '';
  }, []);

  const handleQuote = useCallback((item: CatalogItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    quoteToggle({
      id: item.id,
      title: item.title,
      sku: item.sku,
      brand: config.brandName,
      image: item.images?.[0],
      category: item.groupLabel || item.groupKey,
    });
  }, [quoteToggle, config.brandName]);

  /* ── ESC key ── */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeItem();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [closeItem]);

  /* ── Scroll to top on page change ── */
  const scrollToTop = () => window.scrollTo({ top: 200, behavior: 'smooth' });

  /* ── Panel specs (parsed from HTML or pre-parsed) ── */
  const panelSpecs = useMemo(() => {
    if (!selectedItem) return [];
    if (selectedItem.specs && Object.keys(selectedItem.specs).length > 0) {
      return Object.entries(selectedItem.specs)
        .filter(([, v]) => v !== '' && v != null)
        .map(([label, value]) => ({ label, value: String(value) }));
    }
    if (selectedItem.rawDescription) {
      return parseSpecs(selectedItem.rawDescription);
    }
    return [];
  }, [selectedItem]);

  const panelFeatures = useMemo(() => {
    if (!selectedItem) return [];
    if (selectedItem.features?.length) return selectedItem.features;
    if (selectedItem.rawDescription) return parseFeatures(selectedItem.rawDescription);
    return selectedItem.tags?.filter(t => !/^yaz-\d+/i.test(t)) || [];
  }, [selectedItem]);

  const panelDescription = useMemo(() => {
    if (!selectedItem) return '';
    if (selectedItem.description) return selectedItem.description;
    if (selectedItem.rawDescription) return parseDescription(selectedItem.rawDescription);
    return '';
  }, [selectedItem]);

  /* ── CSS vars ── */
  const cssVars = {
    '--brand-color': config.accentColor,
    '--brand-light': config.accentLight,
    '--brand-mid': config.accentMid,
  } as React.CSSProperties;

  /* ── Pagination pages ── */
  const paginationPages = (): (number | '...')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [];
    if (safePage <= 4) pages.push(1, 2, 3, 4, 5, '...', totalPages);
    else if (safePage >= totalPages - 3) pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    else pages.push(1, '...', safePage - 1, safePage, safePage + 1, '...', totalPages);
    return pages;
  };

  /* ─────────────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────────────── */
  return (
    <div className="bc-page" style={cssVars}>

      {/* ── Brand Hero ── */}
      <div className="bc-hero">
        <div className="bc-hero-inner">
          <div>
            <div className="bc-brand-pill">
              <IconTag /> {config.tagline}
            </div>
            <h1 className="bc-brand-name">
              {config.brandName.split(' ')[0]}&nbsp;
              <span>{config.brandName.split(' ').slice(1).join(' ')}</span>
            </h1>
            <p className="bc-brand-tagline">
              {filteredItems.length.toLocaleString('tr')} ürün kataloğu
            </p>
          </div>
          <div className="bc-hero-stats">
            <div className="bc-stat">
              <span className="bc-stat-num">{items.length.toLocaleString('tr')}</span>
              <span className="bc-stat-label">Ürün</span>
            </div>
            {visibleFilterGroups[0] && (
              <div className="bc-stat">
                <span className="bc-stat-num">
                  {(filterOptions[visibleFilterGroups[0].label] || []).length}
                </span>
                <span className="bc-stat-label">{visibleFilterGroups[0].label}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Content Layout ── */}
      <div className="bc-content">

        {mobileSidebarOpen && (
          <button
            type="button"
            className="bc-sidebar-backdrop"
            aria-label="Filtreleri kapat"
            onClick={closeMobileFilters}
          />
        )}

        {/* ── Left Sidebar ── */}
        <aside className={`bc-sidebar${mobileSidebarOpen ? ' mobile-open' : ''}`} aria-hidden={!mobileSidebarOpen && undefined}>
          {mobileSidebarOpen && (
            <div className="bc-sidebar-mobile-header">
              <span className="bc-sidebar-mobile-title">Filtreler</span>
              <button type="button" className="bc-sidebar-mobile-close" onClick={closeMobileFilters} aria-label="Kapat">
                <IconX size={16} />
              </button>
            </div>
          )}

          {/* Filter Groups */}
          {visibleFilterGroups.map(fg => {
            const opts = filterOptions[fg.label] || [];
            const selected = activeFilters[fg.label] || new Set();
            const isOpen = openFilterGroups.has(fg.label);
            return (
              <div key={fg.label} className="bc-filter-group">
                <button
                  type="button"
                  className={`bc-filter-group-header${isOpen ? ' open' : ''}`}
                  onClick={() => setOpenFilterGroups(prev => {
                    const next = new Set(prev);
                    if (next.has(fg.label)) next.delete(fg.label);
                    else next.add(fg.label);
                    return next;
                  })}
                >
                  <span className="bc-filter-group-title">{fg.label}</span>
                  {selected.size > 0 && (
                    <span className="bc-filter-selected-badge">{selected.size}</span>
                  )}
                  <IconChevron open={isOpen} />
                </button>
                {isOpen && (
                  <div className="bc-filter-list">
                    {opts.map(opt => {
                      const checked = selected.has(opt.value);
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          className={`bc-filter-opt${checked ? ' active' : ''}${opt.count === 0 ? ' disabled' : ''}`}
                          disabled={opt.count === 0}
                          onClick={() => toggleFilter(fg.label, opt.value)}
                        >
                          <span className="bc-filter-opt-label">
                            <span className="bc-filter-opt-check">
                              {checked && <IconCheck />}
                            </span>
                            <span className="bc-filter-opt-text" title={opt.value}>
                              {opt.value}
                            </span>
                          </span>
                          <span className="bc-filter-opt-count">{opt.count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {(activeFilterCount > 0 || search) && (
            <button type="button" className="bc-sidebar-clear" onClick={clearFilters}>
              <IconX size={12} /> Filtreleri Temizle ({activeFilterCount})
            </button>
          )}

          {mobileSidebarOpen && (
            <div className="bc-sidebar-mobile-footer">
              <button type="button" className="bc-sidebar-apply" onClick={closeMobileFilters}>
                {filteredItems.length.toLocaleString('tr')} ürünü göster
              </button>
            </div>
          )}
        </aside>

        {/* ── Main ── */}
        <main className="bc-main">

          {/* Top Bar */}
          <div className="bc-topbar">
            <div className="bc-topbar-row">
              <div className="bc-global-search">
                <IconSearch />
                <input
                  ref={searchRef}
                  type="search"
                  placeholder={config.searchPlaceholder || 'Ürün veya kod ara…'}
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  aria-label="Ürün ara"
                />
                {search && (
                  <button type="button" className="bc-search-clear" onClick={() => { setSearch(''); setPage(1); }} aria-label="Aramayı temizle">
                    <IconX size={12} />
                  </button>
                )}
              </div>

              <button
                type="button"
                className="bc-mobile-filter-btn"
                onClick={() => setMobileSidebarOpen(true)}
              >
                <IconFilter />
                Filtrele
                {activeFilterCount > 0 && (
                  <span className="bc-filter-selected-badge">{activeFilterCount}</span>
                )}
              </button>

              <p className="bc-result-count">
                <strong>{filteredItems.length.toLocaleString('tr')}</strong> ürün
              </p>
            </div>

            {activeFilterCount > 0 && (
              <div className="bc-active-filters">
                {Object.entries(activeFilters).flatMap(([label, values]) =>
                  Array.from(values).map(val => (
                    <button
                      type="button"
                      key={`${label}:${val}`}
                      className="bc-active-filter-chip"
                      onClick={() => toggleFilter(label, val)}
                      title={`${label}: ${val}`}
                    >
                      <span className="bc-active-filter-chip-label">{label}:</span>
                      <span className="bc-active-filter-chip-value">{val}</span>
                      <IconX size={10} />
                    </button>
                  ))
                )}
                <button type="button" className="bc-active-filter-clear" onClick={clearFilters}>
                  Tümünü temizle
                </button>
              </div>
            )}
          </div>

          {/* Product Grid */}
          {paged.length === 0 ? (
            <div className="bc-grid">
              <div className="bc-empty">
                <IconSearch />
                <p>Sonuç bulunamadı</p>
                <button className="bc-empty-reset" onClick={clearFilters}>
                  <IconX size={12} /> Filtreleri Temizle
                </button>
              </div>
            </div>
          ) : (
            <div className="bc-grid">
              {paged.map(item => (
                <ProductCard
                  key={item.id}
                  item={item}
                  isInQuote={has(item.id)}
                  onClick={() => openItem(item)}
                  onQuote={e => handleQuote(item, e)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bc-pagination">
              <button
                className="bc-page-btn"
                disabled={safePage === 1}
                onClick={() => { setPage(p => p - 1); scrollToTop(); }}
              >
                ← Önceki
              </button>
              {paginationPages().map((pg, i) =>
                pg === '...'
                  ? <span key={`e${i}`} className="bc-page-ellipsis">…</span>
                  : <button
                      key={pg}
                      className={`bc-page-num${safePage === pg ? ' active' : ''}`}
                      onClick={() => { setPage(pg as number); scrollToTop(); }}
                    >
                      {pg}
                    </button>
              )}
              <button
                className="bc-page-btn"
                disabled={safePage === totalPages}
                onClick={() => { setPage(p => p + 1); scrollToTop(); }}
              >
                Sonraki →
              </button>
            </div>
          )}
        </main>
      </div>

      {/* ── Quote FAB ── */}
      {quoteCount > 0 && (
        <button className="bc-quote-fab" onClick={openDrawer}>
          <IconCart />
          Teklif Sepeti
          <span className="bc-quote-fab-badge">{quoteCount}</span>
        </button>
      )}

      {/* ── Product Detail Panel ── */}
      {selectedItem && (
        <>
          <div className="bc-panel-backdrop" onClick={closeItem} />
          <aside className="bc-panel" role="dialog" aria-modal="true" aria-label={selectedItem.title}>
            {/* Header */}
            <div className="bc-panel-header">
              <span className="bc-panel-brand-label">{config.brandName}</span>
              <button className="bc-panel-close" onClick={closeItem} aria-label="Kapat">
                <IconX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="bc-panel-body">
              {/* Gallery */}
              <div className="bc-panel-gallery">
                <div className="bc-panel-main-img">
                  {selectedItem.images?.length ? (
                    <img
                      src={selectedItem.images[panelImg] || selectedItem.images[0]}
                      alt={selectedItem.title}
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d1d5db', height: '100%' }}>
                      <IconImage />
                    </div>
                  )}
                </div>
                {selectedItem.images && selectedItem.images.length > 1 && (
                  <div className="bc-panel-thumbs">
                    {selectedItem.images.map((img, i) => (
                      <div
                        key={i}
                        className={`bc-panel-thumb${panelImg === i ? ' active' : ''}`}
                        onClick={() => setPanelImg(i)}
                      >
                        <img src={img} alt="" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="bc-panel-info">
                {/* Badges */}
                <div className="bc-panel-badges">
                  <span className="bc-pi-badge brand">{config.brandName}</span>
                  {selectedItem.groupLabel && (
                    <span className="bc-pi-badge cat">{selectedItem.groupLabel}</span>
                  )}
                  {selectedItem.badge && (
                    <span className="bc-pi-badge promo">{selectedItem.badge}</span>
                  )}
                </div>

                {/* Title */}
                <h2 className="bc-panel-title">{selectedItem.title}</h2>

                {/* SKU */}
                {selectedItem.sku && (
                  <div className="bc-panel-sku">
                    <IconTag /> {selectedItem.sku}
                  </div>
                )}

                {/* Technical Specs */}
                {panelSpecs.length > 0 && (
                  <div>
                    <div className="bc-section-label">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                      </svg>
                      Teknik Özellikler
                    </div>
                    <div className="bc-specs-table">
                      {panelSpecs.map(({ label, value }, i) => (
                        <div key={i} className="bc-spec-row">
                          <span className="bc-spec-label">{label}</span>
                          <span className="bc-spec-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                {panelFeatures.length > 0 && (
                  <div>
                    <div className="bc-section-label">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 11 12 14 22 4"/>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                      </svg>
                      Özellikler
                    </div>
                    <div className="bc-feature-tags">
                      {panelFeatures.map((f, i) => (
                        <span key={i} className="bc-feature-tag">{f}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                {panelDescription && (
                  <div>
                    <div className="bc-section-label">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      Ürün Açıklaması
                    </div>
                    <p className="bc-panel-desc">{panelDescription}</p>
                  </div>
                )}
              </div>
            </div>

            {/* CTA Footer */}
            <div className="bc-panel-cta">
              <button
                className={`bc-cta-quote${has(selectedItem.id) ? ' added' : ''}`}
                onClick={() => handleQuote(selectedItem)}
              >
                {has(selectedItem.id) ? (
                  <><IconCheck2 /> Teklif Sepetinde</>
                ) : (
                  <><IconPhone /> Fiyat Teklifi İste</>
                )}
              </button>
              {selectedItem.pdfUrl && (
                <a
                  href={selectedItem.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bc-cta-pdf"
                >
                  <IconPdf /> PDF
                </a>
              )}
            </div>
          </aside>
        </>
      )}

      {/* Quote Drawer lives in App via QuoteProvider */}
    </div>
  );
}

/* ============================================================
   PRODUCT CARD
   ============================================================ */
interface ProductCardProps {
  item: CatalogItem;
  isInQuote: boolean;
  onClick: () => void;
  onQuote: (e: React.MouseEvent) => void;
}

function ProductCard({ item, isInQuote, onClick, onQuote }: ProductCardProps) {
  const img = item.images?.[0];

  return (
    <div
      className={`bc-card${isInQuote ? ' in-quote' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <div className="bc-card-img-wrap">
        {img ? (
          <img
            src={img}
            alt={item.title}
            className="bc-card-img"
            loading="lazy"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div className="bc-card-no-img">
            <IconImage />
          </div>
        )}
        {item.badge && <div className="bc-card-badge">{item.badge}</div>}
        {item.groupLabel && <div className="bc-card-cat">{item.groupLabel}</div>}

        {/* Hover overlay */}
        <div className="bc-card-overlay">
          <button
            className="bc-card-overlay-btn view"
            onClick={e => { e.stopPropagation(); onClick(); }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
            İncele
          </button>
          <button
            className={`bc-card-overlay-btn quote${isInQuote ? ' in-quote' : ''}`}
            onClick={onQuote}
          >
            {isInQuote
              ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5"/></svg> Eklendi</>
              : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg> Teklif</>
            }
          </button>
        </div>

        {/* In-quote indicator */}
        <div className="bc-card-quote-dot">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
        </div>
      </div>

      <div className="bc-card-body">
        <div className="bc-card-title">{item.title}</div>
        {item.subtitle && <div className="bc-card-subtitle">{item.subtitle}</div>}
        {item.sku && <span className="bc-card-sku">{item.sku}</span>}
      </div>
    </div>
  );
}
